'use memo'

import { useEffect, useRef } from 'react'

/**
 * Reports an element's rendered height whenever it changes — for a grid row
 * that has to be as tall as its content (a nested table), which AG Grid
 * Community cannot size by itself for a full-width row.
 *
 * @param onHeight - called with the height in whole pixels
 */
export const useMeasuredRowHeight = <T extends HTMLElement>(
  onHeight: (height: number) => void
) => {
  'use memo'

  const ref = useRef<T>(null)
  // The latest callback, so a new one never re-subscribes the observer.
  const onHeightRef = useRef(onHeight)
  useEffect(() => {
    onHeightRef.current = onHeight
  })

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return undefined
    }
    let frame = 0
    const observer = new ResizeObserver(([entry]) => {
      const height =
        entry.borderBoxSize?.[0]?.blockSize ??
        element.getBoundingClientRect().height
      // Resizing the row from inside the callback changes layout again before
      // the browser has delivered every notification ("ResizeObserver loop").
      // The next frame is after that delivery.
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() =>
        onHeightRef.current(Math.ceil(height))
      )
    })
    observer.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  return ref
}
